import { useEffect, useRef, useState } from "react";
import { seqOf, tapLabel } from "../game/special-seqs";
import { DEFAULT_SPECIAL_SEQS, type Dir, type SpecialSeqs, type SpecialTrick } from "../game/tricks";
import { RIDES, type RideId } from "../game/sim/rides";
import { buttonFor, INPUT_KINDS, INPUT_LABELS, namedFor, type InputKind } from "./input-names";
import { PANEL } from "./theme";

type Row = [pad: string, keys: string, action: string, touch: string | null];

/** Where the special rows go in `ROWS`: they're built from the player's sequences (#135). */
const SPECIALS_HERE: Row = ["", "", "", null];

const PAD_BUTTON: Record<SpecialTrick["button"], string> = { grab: "○", flip: "□", grind: "△" };
const KEY_BUTTON: Record<SpecialTrick["button"], string> = { grab: "K", flip: "J", grind: "L" };
const TOUCH_BUTTON: Record<SpecialTrick["button"], string> = { grab: "Grab", flip: "Flip", grind: "Grind" };
const WASD: Record<Dir, string> = { u: "W", d: "S", l: "A", r: "D", n: "" };
const SPECIAL_DOES: Record<string, string> = {
  "Christ Air": "Special: Christ Air, big points · let go in time to get back on the board or you bail",
  McTwist: "Special: McTwist (inverted 540) · needs a big vert air, it has to finish before you land",
  "900": "Special: 900 · needs the biggest vert air, it has to finish before you land",
  "Kickflip Underflip": "Special: Kickflip Underflip",
  Darkslide: "Special grind: Darkslide",
  // The bike's (#138): head over heels, hands on the bars; down before it comes round is a bail.
  Flair: "Special: Flair, a backflip with a 180 · wants vert, it brings you back down the ramp",
  "Double Backflip": "Special: Double Backflip · needs a big crouched air, it has to finish before you land",
  "Double Frontflip": "Special: Double Frontflip · needs a big crouched air, it has to finish before you land",
};

/** A row per special in a ride's table on these sequences; the first says where to change them. */
function specialRows(seqs: SpecialSeqs, table: readonly SpecialTrick[]): Row[] {
  return table.map((t, i): Row => {
    const [a, b] = seqOf(seqs, t);
    const taps = `${tapLabel(a)} ${tapLabel(b)}`;
    const hold = t.kind === "grab" ? " (hold)" : t.button === "grind" ? " onto a rail" : "";
    const does = SPECIAL_DOES[t.name] ?? `Special: ${t.name}`;
    return [
      `Meter full: ${taps} then ${PAD_BUTTON[t.button]}${hold}`,
      `${WASD[a]} ${WASD[b]} then ${KEY_BUTTON[t.button]}`,
      i === 0 ? `${does} · change them in Settings > Controls > Specials` : does,
      `${i === 0 ? "Flip, Grab and Grind glow: stick" : "Meter full: stick"} ${taps} then ${TOUCH_BUTTON[t.button]}${hold}`,
    ];
  });
}

/** Pad, keyboard, what it does, and the same on the touch controls (null: not on glass). */
export const ROWS: Row[] = [
  ["Left stick / D-pad", "Arrows / WASD", "Steer · brake (down) · spin in air. You roll forward on your own", "Drag anywhere on the stick side"],
  ["✕  hold, release", "Space", "Crouch to build speed, release to ollie (hold longer = higher)", "Ollie: hold, let go (the ring fills as you crouch)"],
  ["Holding ✕: ↑ ↑ or ↓ ↓, then release", "Space + W W / S S", "Boneless / Fastplant: a higher pop · an ollie while rolling fakie is a Fakie Ollie", "Holding Ollie: stick ↑ ↑ or ↓ ↓, then let go"],
  ["□  + direction", "J", "Flip trick: Kickflip ← (or no direction), Heelflip →, Impossible ↑, Shove-It ↓ (tap twice for a 360 Shove-It), and diagonals: Hardflip ↖, 360 Flip ↗, Varial Kickflip ↙, Varial Heelflip ↘", "Flip + stick direction"],
  ["○  + direction (hold)", "K", "Grab: Method, Indy, Melon, Nosegrab, Tailgrab", "Grab (hold) + stick direction"],
  SPECIALS_HERE,
  ["△  + direction", "L / I", "Grind a rail, ledge or coping · diagonals: Crooked ↖, Overcrook ↗, Smith ↙, Feeble ↘ · balance with left/right · press again with a new direction to switch grinds · ✕ ollies off (hold left or right to go that way), L1 / R1 hop off to that side", "Grind (glows near a rail) + stick direction · on a rail, L or R hops off to that side"],
  ["△ (+ direction) at the coping, going straight up", "L / I", "Lip trick: Axle Stall, Invert ↑, Rock to Fakie ↓, Disaster ←, Nose Stall → · balance with left/right · let go of △ (or press ✕) to drop back in, then revert or manual", "Grind (+ stick) at the coping, going straight up"],
  ["↑ held as you leave a quarter pipe", "W / ↑ held", "Transfer out over the deck: land down a ramp across the gap for a Transfer · without ↑ vert comes straight back down", "Stick ↑ held as you leave a quarter pipe"],
  ["↑ ↓ or ↓ ↑ quickly", "W S / S W", "Manual / nose manual (on the ground, or just before landing) · balance with up/down, ollie out to keep the combo", "Stick ↑ ↓ or ↓ ↑ quickly"],
  ["↑ held right behind a moving cart", "W / ↑ held", "Skitch: hang onto its back end and get towed, scoring as you go · balance with left/right (its turns throw you out) · ↓ lets go, or crouch and release ✕ to pop off, faster than it", "Stick ↑ right behind a moving cart"],
  ["In a manual: □ / ○ + direction", "J / K", "Ground tricks: □ pops Kickflip, Heelflip →, Impossible ↑, Shove-It ↓, 360 Shove-It ←, Hardflip ↖, 360 Flip ↗, Varial Kickflip ↙, Varial Heelflip ↘ · hold ○ for Casper, Pogo ↑, Truckstand ↓, Primo ←, Anti-Casper →", "In a manual: Flip / Grab + stick"],
  ["✕ or △ at a wall", "Space / L", "Wallplant: jump at a wall, press as you hit it to kick off", "Ollie or Grind as you hit a wall"],
  ["Hold △ into a wall at an angle, in the air", "L / I", "Wallride along it · ✕ to Wallie off, or ride it out", "Hold Grind into a wall, in the air"],
  ["L1 / R1", "Q / E", "Fast spin left / right · on a rail, hop off to that side · on foot, switch what's out of the backpack: the board, the paintball gun (□ fires), the scooter, the power washer (hold □ to spray paint off), the roller skates or the BMX bike", "L / R"],
  ["R2", "O", "Revert: tap as you land on a ramp to pivot out of fakie · in the air off a spine, transfer over · in the air over a ramp from flat or a deck, acid drop into it · switch stance on flat", "Revert (pulses in its window)"],
  ["L2", "U", "Nollie stance: your next ollie pops off the nose", "Nollie (lit while it's on)"],
  ["L2 + R2 together", "U + O", "Step off the board (on flat ground; mid-combo, just landed or in a manual, the combo waits a few seconds for a Caveman or getting back on) and walk: stick up runs, part way walks, down backs up, ✕ jumps · L2 + R2 again to drop the board and roll off, or in a jump for a Caveman", "Nollie + Revert together"],
  ["Start", "Esc / P", "Pause menu: resume, reset skater, settings (volume, camera, remap buttons)", "❚❚ (top right) · Settings has touch size, opacity and left-handed"],
  ["—", "R", "Reset skater (quick key)", null],
  ["Select", "H", "Show / hide this help", "? (top right), or tap outside this panel"],
];

/**
 * On the BMX (#141) these rows say what the bike does instead (keyed by the
 * pad column); null drops a row the bike doesn't have.
 */
const BIKE_ROWS: Record<string, Row | null> = {
  "✕  hold, release": ["✕  hold, release", "Space", "Crouch to pedal faster, release to bunny hop (higher than an ollie)", "Ollie: hold, let go"],
  "Holding ✕: ↑ ↑ or ↓ ↓, then release": null,
  "□  + direction": ["□  + direction", "J", "Whip the bike: Tailwhip ← (or no direction), Barspin →, Turndown ↑, Reverse Tailwhip ↓, and diagonals: Tailwhip Barspin ↖, Turndown Barspin ↗, Reverse Whip Barspin ↘ (tap twice for a Double)", "Flip + stick direction"],
  "○  + direction (hold)": ["○  + direction (hold)", "K", "No-Hander, X-Up ↑, Superman ↓, One-Footer ←, Can-Can → · hold L1 or R1 as you grab for the bigger one: Nothing, X-Up Nac-Nac ↑, Hart Attack ↓, One-Foot X-Up ←, No-Hand Can-Can →", "Grab (hold) + stick direction (hold L or R too for the bigger one)"],
  "△  + direction": ["△  + direction", "L / I", "Double Peg: both pegs on a rail, ledge or coping · balance with left/right", "Grind (glows near a rail)"],
  "△ (+ direction) at the coping, going straight up": ["△ (+ direction) at the coping, going straight up", "L / I", "Peg stall: Tooth-Pick, X-Up Stall ↑, Tooth-Pick to Fakie ↓, Pedal Stall ←, Nose-Pick → · let go of △ to drop back in", "Grind (+ stick) at the coping, going straight up"],
  "↑ ↓ or ↓ ↑ quickly": ["↑ ↓ or ↓ ↑ quickly", "W S / S W", "Manual on the back wheel / nose manual on the front · balance with up/down, hop out to keep the combo", "Stick ↑ ↓ or ↓ ↑ quickly"],
  "In a manual: □ / ○ + direction": ["In a manual: □ / ○ + direction", "J / K", "□ whips or spins the bike and lands back on the one wheel (the same moves as in the air) · hold ○ for No-Hand Manual, X-Up Manual ↑, Can-Can Manual ↓, One-Foot Manual ←, One-Foot No-Hander → · in a nose manual (an endo): No-Hand Endo, X-Up Endo ↑, Can-Can Endo ↓, One-Foot Endo ←, Endo Nac-Nac →", "In a manual: Flip / Grab + stick"],
  "L1 / R1": ["L1 / R1", "Q / E", "Fast spin left / right · held as you grab, the bigger grab (the modifier) · on a rail, hop off to that side · on foot, switch what's out of the backpack", "L / R"],
  L2: null,
  "L2 + R2 together": ["L2 + R2 together", "U + O", "Get off the bike and walk (it goes in the backpack) · L1 / R1 on foot picks the board or the bike, L2 + R2 again gets on", "Nollie + Revert together"],
};

/**
 * On the scooter (#139) these rows say what it does instead, as the bike's do.
 */
const SCOOTER_ROWS: Record<string, Row | null> = {
  "✕  hold, release": ["✕  hold, release", "Space", "Crouch to build speed (otherwise you kick along), release to hop (lower than an ollie)", "Ollie: hold, let go"],
  "Holding ✕: ↑ ↑ or ↓ ↓, then release": null,
  "□  + direction": ["□  + direction", "J", "Whip it: Tailwhip ← (or no direction), Barspin →, Bri Flip ↑, Heel Whip ↓, and diagonals: Finger Whip ↖, Tailwhip Barspin ↗, Heel Whip Barspin ↘ (tap twice for a Double)", "Flip + stick direction"],
  "○  + direction (hold)": ["○  + direction (hold)", "K", "No-Hander, Superman ↑, No-Footer ↓, One-Footer ←, Can-Can →", "Grab (hold) + stick direction"],
  "△  + direction": ["△  + direction", "L / I", "Grind on the deck: 50-50, Nosegrind ↑, 5-0 ↓, Boardslide ←, Lipslide →, Crooked ↖, Overcrook ↗, Smith ↙, Feeble ↘ · balance with left/right · press again with a new direction to switch", "Grind (glows near a rail) + stick direction"],
  "△ (+ direction) at the coping, going straight up": ["△ (+ direction) at the coping, going straight up", "L / I", "Stall: Tail Stall, Disaster ↑, Fakie Tail Stall ↓, Deck Stall ←, Nose Stall → · let go of △ to drop back in", "Grind (+ stick) at the coping, going straight up"],
  "↑ ↓ or ↓ ↑ quickly": ["↑ ↓ or ↓ ↑ quickly", "W S / S W", "Manual on the back wheel / nose manual on the front · balance with up/down, hop out to keep the combo", "Stick ↑ ↓ or ↓ ↑ quickly"],
  "In a manual: □ / ○ + direction": ["In a manual: □ + direction", "J", "□ whips the scooter and lands back on the back wheel (the same moves as in the air)", "In a manual: Flip + stick"],
  L2: null,
  "L2 + R2 together": ["L2 + R2 together", "U + O", "Get off the scooter and walk (it goes in the backpack) · L1 / R1 on foot picks what's out, L2 + R2 again gets on", "Nollie + Revert together"],
};

/** On roller skates (#140), likewise. */
const SKATES_ROWS: Record<string, Row | null> = {
  "Left stick / D-pad": ["Left stick / D-pad", "Arrows / WASD", "Steer · brake (down) · spin in air. You stride along on your own; rolling backwards is no harder (no switch on skates)", "Drag anywhere on the stick side"],
  "Holding ✕: ↑ ↑ or ↓ ↓, then release": null,
  "□  + direction": ["□  + direction", "J", "Throw the body round: Flatspin (no direction), Misty Flip ←, Rodeo Flip →, Corkscrew ↑, Bio Flip ↓ (tap twice for a Double; they want a kicker or vert)", "Flip + stick direction"],
  "○  + direction (hold)": ["○  + direction (hold)", "K", "Grab a skate: Mute, Japan ↑, Rocket ↓, Stale ←, Liu Kang →", "Grab (hold) + stick direction"],
  "△  + direction": ["△  + direction", "L / I", "Soul plate tricks: Soul, Topsoul ↑, Makio ↓, Frontside ←, Backside →, Royale ↖, Unity ↗, Torque Soul ↙, Sweatstance ↘ · balance with left/right · press again with a new direction to switch", "Grind (glows near a rail) + stick direction"],
  "△ (+ direction) at the coping, going straight up": ["△ (+ direction) at the coping, going straight up", "L / I", "Stall: Soul Stall, Backside Stall ↑, Fakie Soul Stall ↓, Frontside Stall ←, Toe Stall → · let go of △ to drop back in", "Grind (+ stick) at the coping, going straight up"],
  "↑ ↓ or ↓ ↑ quickly": ["↑ ↓ or ↓ ↑ quickly", "W S / S W", "Manual on the heel wheels / nose manual on the toes · balance with up/down, hop out to keep the combo", "Stick ↑ ↓ or ↓ ↑ quickly"],
  "In a manual: □ / ○ + direction": ["In a manual: □ + direction", "J", "□ hops round: Hop 360, Hop 540 ↓", "In a manual: Flip + stick"],
  L2: null,
  "L2 + R2 together": ["L2 + R2 together", "U + O", "Take the skates off and walk (you carry them) · L1 / R1 on foot picks what's out, L2 + R2 again puts them on", "Nollie + Revert together"],
};

/**
 * The rides' body flips (#150), a row of their own under the flip row so
 * they aren't lost at the end of it: two stick taps then □, in the air.
 */
const FLIP_PAD = "↑ ↓ or ↓ ↑, then □";
const FLIP_KEYS = "W S / S W, J";
const FLIP_TOUCH = "In the air: stick ↑ ↓ or ↓ ↑, then Flip";
const BIKE_FLIPS: Row = [FLIP_PAD, FLIP_KEYS, "In the air: Backflip (↑ ↓) / Frontflip (↓ ↑), no special needed, the bike goes over in the air you've got · □ again mid-flip whips it too (Backflip Tailwhip, tap twice for a Double), or ○ + direction for a grab (Superman Backflip)", FLIP_TOUCH];
const SCOOTER_FLIPS: Row = [FLIP_PAD, FLIP_KEYS, "In the air: Backflip (↑ ↓) / Frontflip (↓ ↑), they want a quarter's air", FLIP_TOUCH];
const SKATES_FLIPS: Row = [FLIP_PAD, FLIP_KEYS, "In the air: Backflip (↑ ↓) / Frontflip (↓ ↑)", FLIP_TOUCH];

/**
 * A ride's own rows and the help's heading on it; the board's are `ROWS` as
 * they are. `after`: rows the ride adds under one of its rows (keyed the same).
 */
export const RIDE_HELP: Partial<Record<RideId, { title: string; rows: Record<string, Row | null>; after: Record<string, Row[]> }>> = {
  bmx: { title: "Controls: BMX", rows: BIKE_ROWS, after: { "□  + direction": [BIKE_FLIPS] } },
  scooter: { title: "Controls: Scooter", rows: SCOOTER_ROWS, after: { "□  + direction": [SCOOTER_FLIPS] } },
  skates: { title: "Controls: Skates", rows: SKATES_ROWS, after: { "□  + direction": [SKATES_FLIPS] } },
};

/** A row's column for each input; null or "—": that input hasn't got it. */
const COLUMN: Record<InputKind, 0 | 1 | 3> = { gamepad: 0, keyboard: 1, touch: 3 };

/**
 * One column, the input you're using (#147), switching live with it; the
 * toggle at the top looks at another until you switch. `remapped`: the player
 * moved buttons in Settings, so the table (the defaults) says so.
 * `touchDevice`: offer the touch controls' column too.
 */
export function ControlsHelp({
  onClose,
  remapped = false,
  input = "keyboard",
  touchDevice = false,
  specials = DEFAULT_SPECIAL_SEQS,
  ride = "board",
  padTip = null,
}: {
  onClose: () => void;
  remapped?: boolean;
  /** The input you're using now (`activeInput`). */
  input?: InputKind;
  touchDevice?: boolean;
  /** The player's special sequences (#135), shown in place of the defaults. */
  specials?: SpecialSeqs;
  /** What the player's riding (#141): the BMX (#138), the scooter (#139) and the skates (#140) have their own trick rows and specials. */
  ride?: RideId;
  /** Until a pad has been seen (#161): a one-line tip at the top. */
  padTip?: string | null;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // A pick from the toggle lasts until the live input changes (reset while rendering, React's way to follow a prop).
  const [pick, setPick] = useState<InputKind | null>(null);
  const [live, setLive] = useState(input);
  if (live !== input) {
    setLive(input);
    setPick(null);
  }
  const shown = live === input ? (pick ?? input) : input;
  const kinds = INPUT_KINDS.filter((k) => k !== "touch" || touchDevice || input === "touch");
  const help = RIDE_HELP[ride];
  const rows = ROWS.flatMap((r) => {
    if (r === SPECIALS_HERE) return specialRows(specials, RIDES[ride].tricks.specials);
    if (help && r[0] in help.rows) {
      const swap = help.rows[r[0]];
      return swap ? [swap, ...(help.after[r[0]] ?? [])] : [];
    }
    return [r];
  }).flatMap((r) => {
    const how = r[COLUMN[shown]];
    return how && how !== "—" ? [{ key: r[0], how, does: namedFor(shown, r[2]) }] : [];
  });

  // A tap or click anywhere outside the panel closes it (phones have no H key).
  // The Controls button toggles it itself, so it's left out.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target || panel.current?.contains(target) || target.closest("[data-controls-toggle]")) return;
      onClose();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [onClose]);

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label="Controls"
      // Capped below the screen height with the table scrolling inside, so the
      // Hide button stays on screen on a phone and there's room to tap outside.
      className={`${PANEL} absolute bottom-4 left-4 flex max-h-[75dvh] max-w-[min(92vw,34rem)] flex-col p-4`}
    >
      <div className="mb-2 flex shrink-0 items-center justify-between gap-4">
        <h2 className="text-lg font-black uppercase italic">{help?.title ?? "Controls"}</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border-2 border-line px-2 text-center text-xs font-bold hover:bg-surface-soft"
        >
          {input === "touch" ? "Hide" : `Hide (${buttonFor(input, "Select")})`}
        </button>
      </div>
      <div role="group" aria-label="Show controls for" className="mb-2 flex shrink-0 gap-1">
        {kinds.map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={k === shown}
            onClick={() => setPick(k)}
            className={`rounded-full border-2 px-2 text-center text-xs font-bold ${k === shown ? "border-ink bg-accent text-on-accent" : "border-line hover:bg-surface-soft"}`}
          >
            {INPUT_LABELS[k]}
          </button>
        ))}
      </div>
      <div className="min-h-0 overflow-auto">
        {padTip && (
          <p data-pad-tip className="mb-2 rounded-md bg-surface-soft px-2 py-1 text-xs font-bold">
            🎮 {padTip}
          </p>
        )}
        {remapped && shown !== "touch" && (
          <p className="mb-2 rounded-lg bg-highlight-soft/40 px-2 py-1 text-xs font-bold">
            You&apos;ve remapped buttons in Pause &gt; Settings; this list shows the defaults.
          </p>
        )}
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="uppercase tracking-wide text-on-surface-soft">
              <th className="pb-1 pr-3 font-bold">{INPUT_LABELS[shown]}</th>
              <th className="pb-1 font-bold">Does</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, how, does }) => (
              <tr key={key} className="border-t border-line align-top">
                <td className={`py-1 pr-3 font-bold ${shown === "touch" ? "w-2/5" : shown === "keyboard" ? "font-mono whitespace-nowrap" : "whitespace-nowrap"}`}>{how}</td>
                <td className="py-1">{does}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[11px] text-on-surface-soft">
          Land with the board lined up with where you&apos;re going. Land mid-flip or sideways and you bail. Crouch on the
          way into the big wooden ramps to go vert.
        </p>
      </div>
    </div>
  );
}
